import { Inject, Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Router } from '@angular/router';
import { LogService } from '@core/services/common/log.service';
import { Log } from '@core/models/common/log.model';
import { LogConstants } from '@core/constants/log.constants';
import { MESSAGE_CONSTANT } from '@core/constants/message.constants';
import { AuthService } from '@core/services/auth/auth.service';

interface errorResponse {
  message: string;
  errors?: Record<string, string[]>;
}

@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
  private redirectingToLogin = false;

  constructor(
    private router: Router,
    private logService: LogService,
    private authService: AuthService,
    @Inject(DOCUMENT) private document: Document
  ) {}

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        const structuredError = this.buildErrorObject(req, error);

        const logEntry: Log = {
          channel: LogConstants.CHANNEL,
          message: structuredError.message,
          context: [structuredError],
        };
        this.logService.logError(logEntry);

        if (error.status === 401) {
          this.handleUnauthorized(req);
        }

        if (error.status === 403) {
          this.router.navigate(['/']);
        }

        if (error.status === 0 || error.status === 404) {
          this.router.navigate(['/404']);
        }

        return throwError(() => structuredError);
      })
    );
  }

  private handleUnauthorized(req: HttpRequest<unknown>): void {
    // A 401 from a login request means bad credentials; the login screen shows its own error.
    if (/\/login(\?|$)/.test(req.url) || this.redirectingToLogin) {
      return;
    }
    this.redirectingToLogin = true;

    const returnUrl = this.getCurrentUrl();
    this.authService.logout();
    void Promise.resolve(
      this.router.navigate(['/auth/login'], {
        queryParams: returnUrl.startsWith('/auth/login') ? undefined : { returnUrl },
      })
    ).finally(() => {
      this.redirectingToLogin = false;
    });
  }

  // On a cold load router.url can still be '/' before the first navigation settles.
  private getCurrentUrl(): string {
    const routerUrl = this.router.url;
    if (routerUrl && routerUrl !== '/') {
      return routerUrl;
    }
    const location = this.document.defaultView?.location;
    return location ? `${location.pathname}${location.search}${location.hash}` : routerUrl || '/';
  }

  private buildErrorObject(req: HttpRequest<unknown>, error: HttpErrorResponse) {
    const apiError = error.error as errorResponse;
    return {
      url: req.url,
      method: req.method,
      status: error.status,
      message: this.getErrorMessage(error),
      validationErrors: apiError.errors || null,
      timestamp: new Date().toISOString(),
    };
  }

  private getErrorMessage(error: HttpErrorResponse): string {
    if (typeof ErrorEvent !== 'undefined' && error.error instanceof ErrorEvent) {
      return `Client-side error: ${error.error.message}`;
    }

    switch (error.status) {
      case 400:
        return MESSAGE_CONSTANT.BAD_REQUEST;
      case 401:
        return MESSAGE_CONSTANT.UNAUTHORIZED;
      case 403:
        return MESSAGE_CONSTANT.FORBIDDEN;
      case 404:
        return MESSAGE_CONSTANT.NOT_FOUND;
      case 422:
        return MESSAGE_CONSTANT.VALIDATION_ERROR;
      case 500:
        return MESSAGE_CONSTANT.INTERNAL_SERVER_ERROR;
      default:
        return `Error Code: ${error.status}\nMessage: ${error.message}`;
    }
  }
}
